import React from 'react';

import { useIntl } from 'react-intl-next';

import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import {
	getUrlMaybe,
	type GlobalShortcutLinkItem,
	type GlobalShortcutsItem,
} from '@atlassian/global-shortcuts';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';

import type {
	ButtonItemConfig,
	ExpandableItemConfig,
	FlyoutItemConfig,
	LinkItemConfig,
	NavItemConfig,
	ObjectListFlyoutItemConfig,
} from '../../../common/types';
import NavItem from '../../../common/ui/nav-item';
import {
	isButtonItemConfig,
	isCustomItemConfig,
	isExpandableItemConfig,
	isFlyoutItemConfig,
	isLinkItemConfig,
	isObjectListFlyoutItemConfig,
} from '../../../common/utils/type-guards';
import { useSetPeekMenuId } from '../../../controllers/peek-menu-id-providers';

import { messages } from './messages';

export interface MoreMenuItemProps {
	id: string;
	items: Record<string, NavItemConfig>;
	// This onClick is passed from the MoreMenu to close the menu after clicking an item
	onClick?: () => void;
	openShortcutsInSameTab?: boolean;
	shortcutItems?: Record<string, GlobalShortcutsItem>;
}

export default function MoreMenuItem({
	id,
	items,
	shortcutItems,
	onClick,
	openShortcutsInSameTab,
}: MoreMenuItemProps): React.ReactNode {
	const { formatMessage } = useIntl();
	const setPeekMenuId = useSetPeekMenuId();

	const shortcutItem = shortcutItems?.[id];

	if (shortcutItem) {
		const moreItem = buildMoreShortcutItem({ shortcutItem, formatMessage, openShortcutsInSameTab });
		return <NavItem id={id} item={moreItem} isInMoreMenu group={L1_MENU_GROUPS.SHORTCUTS} />;
	}

	// Check if the item is in the items map
	const item = items[id];

	if (!item) {
		throw new Error(
			`NavItem: No item found for id "${id}". Ensure the item is defined in appItems or globalItems.`,
		);
	}

	if (isCustomItemConfig(item)) {
		return (
			<NavItem
				id={id}
				item={item}
				isInMoreMenu
				onPeek={() => setPeekMenuId(id)}
				onClick={onClick}
			/>
		);
	}

	const moreItem = buildMoreItem({ id, item, onClick, onPeek: setPeekMenuId });
	return <NavItem id={id} item={moreItem} isInMoreMenu />;
}

function buildMoreShortcutItem({
	formatMessage,
	openShortcutsInSameTab,
	shortcutItem,
}: {
	formatMessage: (messageDescriptor: { defaultMessage?: string; id: string }) => string;
	openShortcutsInSameTab?: boolean;
	shortcutItem: GlobalShortcutsItem;
}): NavItemConfig {
	const elemAfter = openShortcutsInSameTab ? undefined : (
		<LinkExternalIcon
			size="small"
			label={formatMessage(messages.linkExternalIconLabel)}
			spacing="spacious"
		/>
	);

	return {
		type: 'link',
		elemAfter,
		elemBefore: shortcutItem.icon,
		label: shortcutItem.label,
		target: openShortcutsInSameTab ? '_self' : '_blank',
		href: getUrlMaybe(shortcutItem as GlobalShortcutLinkItem) || '#',
	};
}

function buildMoreItem({
	id,
	item,
	onClick,
	onPeek,
}: {
	id: string;
	item: NavItemConfig;
	onClick?: () => void;
	onPeek?: (id: string) => void;
}): NavItemConfig {
	if (
		isExpandableItemConfig(item) ||
		isFlyoutItemConfig(item) ||
		isObjectListFlyoutItemConfig(item)
	) {
		return buildMoreItemFromExpandableOrFlyout({ id, item, onClick, onPeek });
	} else if (isButtonItemConfig(item) || isLinkItemConfig(item)) {
		return buildMoreItemFromButtonOrLink({ item, onClick });
	}

	// If the item is not a button, link, expandable, or flyout, return as is
	return item;
}

function buildMoreItemFromExpandableOrFlyout({
	id,
	item,
	onClick,
	onPeek,
}: {
	id: string;
	item: ExpandableItemConfig | FlyoutItemConfig | ObjectListFlyoutItemConfig;
	onClick?: () => void;
	onPeek?: (id: string) => void;
}): ButtonItemConfig {
	const { elemBefore, label } = item;

	const onClickActual = combineHandlerAndActions(
		// ExpandableItemConfig['onClick'] has a third parameter we're not currently providing
		item.onClick as ButtonItemConfig['onClick'],
		onClick,
		onPeek && (() => onPeek(id)),
	);

	// If the item is in the more menu and is an expandable or flyout item, render as a button with peek
	return {
		type: 'button',
		elemBefore,
		label,
		onClick: onClickActual,
	};
}

function buildMoreItemFromButtonOrLink<T extends ButtonItemConfig | LinkItemConfig>({
	item,
	onClick,
}: {
	item: T;
	onClick?: () => void;
}): T {
	const onClickActual = combineHandlerAndActions(item.onClick, onClick);

	return {
		...item,
		// Remove props that are not appropriate for the more menu
		actions: undefined,
		actionsOnHover: undefined,
		elemAfter: undefined,
		labelElem: undefined,
		onClick: onClickActual,
	};
}

const combineHandlerAndActions =
	<T extends (...args: any) => void>(handler?: T, ...actions: ((() => void) | undefined)[]) =>
	(...args: Parameters<T>) => {
		handler?.(...args);
		actions.forEach((action) => {
			action?.();
		});
	};
